/**
 * Protected Route Component
 * Requirements: 13.1
 * 
 * Route guard that redirects unauthenticated users to the login page.
 * Wraps protected content and only renders it when user is authenticated.
 */

import { type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { LoginPage } from './LoginPage';

interface ProtectedRouteProps {
  /** Content to render when authenticated */
  children: ReactNode;
  /** Optional custom fallback component instead of LoginPage */
  fallback?: ReactNode;
}

/**
 * ProtectedRoute wraps content that requires authentication.
 * If the user is not authenticated, it renders the LoginPage (or custom fallback).
 */
export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return fallback ?? <LoginPage />;
  }

  return <>{children}</>;
}
